import { motion } from "framer-motion"
import { Menu } from "lucide-react"
import { useCallback, useEffect, useState } from "react"

import { BrandLogo } from "@/shared/components/BrandLogo"
import { Sheet, SheetTrigger } from "@/shared/components/ui/sheet"
import { ToggleTheme } from "@/shared/components/ui/toggle-theme"
import { buildNav } from "@/shared/i18n/legacyShape"
import { useLocaleCopy } from "@/shared/i18n/useLocaleCopy"
import { cn } from "@/shared/utils"

import { NavbarMobileMenu } from "./NavbarMobileMenu"

const SCROLL_THRESHOLD = 8
const MENU_LABEL = "Abrir menu"
const SECTION_IDS = [
  "como-funciona",
  "api",
  "seguranca",
  "precos",
  "faq",
  "contato",
]

const ACTIVE_LAYOUT_ID = "mesaotc-nav-active-dot"

function useActiveSection() {
  const [active, setActive] = useState<string | null>(null)

  useEffect(() => {
    if (typeof window === "undefined") {return undefined}
    if (typeof IntersectionObserver === "undefined") {return undefined}

    const observed: HTMLElement[] = []
    for (const id of SECTION_IDS) {
      const el = document.getElementById(id)
      if (el) {observed.push(el)}
    }
    if (observed.length === 0) {return undefined}

    const visibility = new Map<string, number>()

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visibility.set(entry.target.id, entry.intersectionRatio)
        }
        let best: { id: string; ratio: number } | null = null
        for (const [id, ratio] of visibility.entries()) {
          if (ratio <= 0.12) {continue}
          if (!best || ratio > best.ratio) {best = { id, ratio }}
        }
        setActive(best?.id ?? null)
      },
      {
        rootMargin: "-40% 0px -40% 0px",
        threshold: [0, 0.15, 0.3, 0.5, 0.8, 1],
      },
    )

    for (const el of observed) {observer.observe(el)}
    return () => observer.disconnect()
  }, [])

  return active
}

export function Navbar() {
  const copy = useLocaleCopy()
  const NAV = buildNav(copy)
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const activeSection = useActiveSection()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > SCROLL_THRESHOLD)
    }
    handleScroll()
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
  }, [])

  return (
    <header className="pointer-events-none fixed inset-x-0 top-0 z-40">
      <div
        className={cn(
          "pointer-events-auto w-full transition-[background-color,border-color,backdrop-filter] duration-[380ms] ease-out",
          scrolled
            ? "border-b border-mesa-border/50 bg-mesa-bg/72 [backdrop-filter:saturate(180%)_blur(14px)] [-webkit-backdrop-filter:saturate(180%)_blur(14px)] dark:border-white/[0.06] dark:[backdrop-filter:saturate(160%)_blur(18px)] dark:[-webkit-backdrop-filter:saturate(160%)_blur(18px)]"
            : "border-b border-transparent bg-transparent",
        )}
      >
        <nav
          aria-label={NAV.ariaPrimary}
          className="mx-auto flex h-14 w-full max-w-7xl items-center justify-between gap-6 px-6 md:px-10"
        >
          <a
            href="#inicio"
            aria-label="MesaOTC — início"
            className={cn(
              "group relative flex cursor-pointer items-center text-mesa-primary transition-opacity duration-200",
              "hover:opacity-70",
              "focus:outline-none focus-visible:outline-none",
            )}
          >
            <BrandLogo size="md" />
          </a>

          <ul className="hidden items-center gap-7 md:flex">
            {NAV.links.map((link, index) => {
              const targetId = link.href.startsWith("#")
                ? link.href.slice(1)
                : null
              const isActive = targetId !== null && activeSection === targetId
              return (
                <li key={link.href} className="relative">
                  <a
                    href={link.href}
                    className={cn(
                      "group relative inline-flex cursor-pointer items-center py-5",
                      "text-[12.5px] font-medium tracking-[0.01em] transition-colors duration-200",
                      "focus:outline-none focus-visible:outline-none",
                      isActive
                        ? "text-mesa-primary"
                        : "text-mesa-text-muted hover:text-mesa-primary",
                    )}
                    aria-current={isActive ? "page" : undefined}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "pointer-events-none absolute -top-2 left-1/2 -translate-x-1/2 font-mono text-[9px] tracking-[0.24em] text-mesa-accent/90 opacity-0 transition-all duration-200",
                        "group-hover:-top-1 group-hover:opacity-100",
                      )}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    {link.label}
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute bottom-3 left-1/2 h-px w-8 -translate-x-1/2 origin-center scale-x-0 bg-mesa-accent/60 transition-transform duration-300 ease-out group-hover:scale-x-100"
                    />
                  </a>
                  {isActive ? (
                    <motion.span
                      layoutId={ACTIVE_LAYOUT_ID}
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 30,
                      }}
                      aria-hidden="true"
                      className="pointer-events-none absolute -left-3 top-1/2 h-1 w-1 -translate-y-1/2 rounded-full bg-mesa-accent"
                    />
                  ) : null}
                </li>
              )
            })}
          </ul>

          <div className="flex items-center gap-3">
            <ToggleTheme className="hidden h-9 w-9 sm:inline-flex" />

            <a
              href={NAV.ghostHref}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "group relative hidden h-9 cursor-pointer items-center justify-center px-2 sm:inline-flex",
                "text-[12.5px] font-medium tracking-[0.01em] text-mesa-text-muted",
                "transition-colors duration-200 hover:text-mesa-primary",
                "focus:outline-none focus-visible:outline-none",
              )}
            >
              {NAV.ghost}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-0.5 left-2 right-2 h-px origin-left scale-x-0 bg-mesa-primary/80 transition-transform duration-[220ms] ease-out group-hover:scale-x-100"
              />
            </a>

            <a
              href="#contato"
              className={cn(
                "hidden h-9 cursor-pointer items-center justify-center rounded-full px-[18px] sm:inline-flex",
                "bg-mesa-primary text-[12px] font-medium tracking-[0.02em] text-primary-foreground",
                "shadow-[0_1px_2px_rgba(11,26,59,0.08)]",
                "transition-[background-color,box-shadow] duration-300 ease-out",
                "hover:bg-mesa-primary-alt hover:shadow-[0_6px_20px_rgba(11,26,59,0.25)]",
                "focus:outline-none focus-visible:outline-none",
              )}
            >
              {NAV.cta}
            </a>

            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label={MENU_LABEL}
                  className={cn(
                    "flex h-9 w-9 cursor-pointer items-center justify-center rounded-full md:hidden",
                    "border border-mesa-border bg-mesa-bg-subtle text-mesa-primary",
                    "transition-colors duration-200 hover:border-mesa-primary/20",
                    "focus:outline-none focus-visible:outline-none",
                  )}
                >
                  <Menu className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
                </button>
              </SheetTrigger>
              <NavbarMobileMenu onClose={closeMenu} />
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  )
}
